import { buildAnnotatedImageAssignments } from '../../utils/annotatedImages';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Send, Loader2, AlertCircle, Bot, ArrowLeft, WifiOff, Sparkles, X, Paperclip, RotateCcw, FileText } from 'lucide-react';
import { Message, BotMessage, UserMessage } from '../../components/features/chat';
import CatalogStatusStrip from '../../components/features/chat/CatalogStatusStrip';
import { OrderProvider } from '../../contexts/OrderContext';
import { finalizeMessage, reduceFrame } from '../../utils/streamReducer';
import { parseStreamEvent } from '../../utils/streamEffects';
import { ContinuationFrame } from '../../types/streamProtocol';
import { usePortalAuth } from '../../contexts/PortalAuthContext';
import {
  createPortalChatSession,
  getPortalChatBaseUrl,
  getPortalChatToken,
} from '../../services/portalApi';
import chatService from '../../services/ChatService';

// ─────────────────────────────────────────────────────────────────────────────
// PortalChatPage — authenticated B2B customer chat, mirrors GuestChatPage SSE
// streaming but uses the portal JWT from localStorage.
// ─────────────────────────────────────────────────────────────────────────────

const BASE_URL = getPortalChatBaseUrl();

const PortalChatPage: React.FC = () => {
  const navigate = useNavigate();
  const { orgAlias } = useParams<{ orgAlias: string }>();
  const { customer } = usePortalAuth();

  // ── Session state ─────────────────────────────────────────────────────────
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [initializing, setInitializing] = useState(true);
  const [initError, setInitError] = useState<string | null>(null);

  // ── Chat state ────────────────────────────────────────────────────────────
  const [messages, setMessages] = useState<Message[]>([]);
  // One assignment pass over the whole thread so a marked photo renders
  // exactly once (it rides along on several messages of the same turn).
  const annotatedImageAssignments = React.useMemo(
    () => buildAnnotatedImageAssignments(messages),
    [messages],
  );
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const isLoadingRef = useRef(false);
  const abortRef = useRef<AbortController | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const sessionCreatingRef = useRef(false);
  const shouldAutoTriggerRef = useRef(false);
  const lastStreamDataRef = useRef<number>(0);

  // ── File state ────────────────────────────────────────────────────────────
  const [selectedFiles, setSelectedFiles] = useState<Array<{ data: string; mimeType: string; preview: string; name: string }>>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // ── Toast state ───────────────────────────────────────────────────────────
  const [toasts, setToasts] = useState<Array<{ id: string; message: string; type: 'error' | 'info' }>>([]);
  const showToast = useCallback((message: string, type: 'error' | 'info' = 'error') => {
    const id = `toast_${Date.now()}`;
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 4500);
  }, []);

  // ── Online state ──────────────────────────────────────────────────────────
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  useEffect(() => {
    const on = () => setIsOnline(true);
    const off = () => setIsOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);

  // ── Scroll ────────────────────────────────────────────────────────────────
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const mainScrollRef = useRef<HTMLElement | null>(null);

  const scrollToBottom = (behavior: ScrollBehavior = 'smooth') =>
    bottomRef.current?.scrollIntoView({ behavior });

  useEffect(() => { scrollToBottom(); }, [messages]);

  useEffect(() => {
    const el = mainScrollRef.current;
    if (!el) return;
    const onScroll = () => {
      setShowScrollBtn(el.scrollHeight - el.scrollTop - el.clientHeight > 200);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length > 0]);

  // ── Keep refs in sync ─────────────────────────────────────────────────────
  useEffect(() => { isLoadingRef.current = isLoading; }, [isLoading]);
  useEffect(() => { sessionIdRef.current = sessionId; }, [sessionId]);

  // Route shared components' section-diagram fetches through the portal endpoint
  // (apiService's Firebase auth doesn't exist here — without this the
  // clarification diagram always renders "Diagram not available").
  useEffect(() => {
    chatService.setGuestAuth(BASE_URL, getPortalChatToken() || null, '/chat/section-diagram');
    return () => chatService.setGuestAuth(BASE_URL, null);
  }, []);

  // No SessionProvider on this page — register the active session id so shared
  // components (diagram fetch) know which session they belong to.
  useEffect(() => {
    chatService.setGuestSessionId(sessionId);
    return () => chatService.setGuestSessionId(null);
  }, [sessionId]);

  // ── Auth header helper — uses portal token from localStorage ─────────────
  const authHeaders = useCallback(
    (extra: Record<string, string> = {}): Record<string, string> => ({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getPortalChatToken()}`,
      ...extra,
    }),
    [],
  );

  // ── Determine language ────────────────────────────────────────────────────
  const language = navigator.language?.slice(0, 2) || 'en';

  // ── Create chat session on mount ──────────────────────────────────────────
  useEffect(() => {
    if (sessionCreatingRef.current) return;
    sessionCreatingRef.current = true;

    (async () => {
      try {
        const data = await createPortalChatSession(language);
        const sid: string = data.session_id;
        sessionIdRef.current = sid;
        setSessionId(sid);
        shouldAutoTriggerRef.current = true;
      } catch (e: any) {
        setInitError(e?.response?.data?.detail ?? e?.message ?? 'Failed to start session.');
      } finally {
        setInitializing(false);
        sessionCreatingRef.current = false;
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Auto-trigger initial greeting after session created ───────────────────
  useEffect(() => {
    if (!sessionId || !shouldAutoTriggerRef.current) return;
    shouldAutoTriggerRef.current = false;
    handleAutoTriggerNext();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // ── Mobile visibility recovery ────────────────────────────────────────────
  useEffect(() => {
    const STALL_ABORT_MS = 30_000;
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      const sid = sessionIdRef.current;
      if (!sid) return;
      if (isLoadingRef.current && abortRef.current) {
        const msSinceData = Date.now() - lastStreamDataRef.current;
        if (msSinceData > STALL_ABORT_MS) {
          abortRef.current.abort();
          abortRef.current = null;
          isLoadingRef.current = false;
          setIsLoading(false);
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // Core SSE streaming — mirrors GuestChatPage.streamToMessage exactly,
  // but POSTs to /api/b2b-portal/chat/endpoint with portal JWT.
  // ─────────────────────────────────────────────────────────────────────────
  const streamToMessage = useCallback(async (
    messageText: string,
    botMsgId: string,
    signal: AbortSignal,
    uploadedFiles?: Array<{ data: string; mimeType: string }>,
    overrideSessionId?: string,
  ) => {
    const STALL_TIMEOUT_MS   =  60_000;
    const FETCH_TIMEOUT_MS   =  20_000;
    const MAX_RETRY_ATTEMPTS = 4;
    const RETRY_BASE_DELAY   = 1_500;
    const RETRY_MAX_DELAY    = 12_000;

    if (!navigator.onLine) throw new Error('offline');

    const clientMessageUuid: string =
      (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
        ? crypto.randomUUID()
        : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

    let lastEventId: string | null = null;
    let retryCount = 0;
    let activeBotId = botMsgId;

    const resetActiveBubble = () => {
      setMessages(prev =>
        prev.map(m => m.id !== activeBotId ? m : { ...m, text: '', events: [], isStreaming: true }),
      );
    };

    let _outerResolve!: () => void;
    let _outerReject!: (err: any) => void;
    const outerPromise = new Promise<void>((res, rej) => { _outerResolve = res; _outerReject = rej; });

    let outerSettled = false;
    const handleOnline = () => {
      if (!outerSettled && retryCount < MAX_RETRY_ATTEMPTS) {
        retryCount++;
        resetActiveBubble();
        setTimeout(() => attemptStream().then(() => { outerSettled = true; _outerResolve(); }).catch(_outerReject), 500);
      }
    };
    window.addEventListener('online', handleOnline);
    outerPromise.finally(() => { outerSettled = true; window.removeEventListener('online', handleOnline); });

    const attemptStream = async (): Promise<void> => {
      let stallTimer:   ReturnType<typeof setTimeout> | null = null;
      let readerRef: ReadableStreamDefaultReader<Uint8Array> | null = null;
      let errorFired = false;
      let stallPausedAt: number | null = null;
      let stallRemainingMs = 0;
      let lastDataReceived = Date.now();
      lastStreamDataRef.current = lastDataReceived;

      const scheduleStallTimeout = (delayMs: number) => {
        if (stallTimer) clearTimeout(stallTimer);
        stallTimer = setTimeout(() => {
          if (signal.aborted) { readerRef?.cancel(); cleanup(); return; }
          readerRef?.cancel();
          if (!errorFired) {
            if (retryCount < MAX_RETRY_ATTEMPTS) {
              errorFired = true;
              cleanup(); readerRef = null;
              retryCount++;
              resetActiveBubble();
              const delay = Math.min(RETRY_BASE_DELAY * Math.pow(2, retryCount - 1), RETRY_MAX_DELAY);
              setTimeout(() => {
                errorFired = false;
                attemptStream().then(() => { outerSettled = true; _outerResolve(); }).catch(_outerReject);
              }, delay);
            } else {
              errorFired = true; cleanup();
              _outerReject(new Error('server_error'));
            }
          }
        }, delayMs);
      };

      const pauseStallTimer = () => {
        if (stallTimer !== null && stallPausedAt === null) {
          clearTimeout(stallTimer); stallTimer = null;
          stallPausedAt = Date.now();
          stallRemainingMs = Math.max(0, STALL_TIMEOUT_MS - (stallPausedAt - lastDataReceived));
        }
      };
      const resumeStallTimer = () => {
        if (stallPausedAt !== null) {
          stallPausedAt = null; lastDataReceived = Date.now();
          scheduleStallTimeout(stallRemainingMs);
        }
      };
      const onActivity = () => {
        lastDataReceived = Date.now();
        lastStreamDataRef.current = lastDataReceived;
        if (document.visibilityState !== 'hidden') scheduleStallTimeout(STALL_TIMEOUT_MS);
      };
      const handleVisibility = () =>
        document.visibilityState === 'hidden' ? pauseStallTimer() : resumeStallTimer();
      document.addEventListener('visibilitychange', handleVisibility);

      const cleanup = () => {
        if (stallTimer)   clearTimeout(stallTimer);
        if (fetchTimeoutId) clearTimeout(fetchTimeoutId);
        document.removeEventListener('visibilitychange', handleVisibility);
      };

      const resumeHeaders: Record<string, string> = {};
      if (lastEventId) resumeHeaders['Last-Event-ID'] = lastEventId;

      const effectiveSessionId = overrideSessionId ?? sessionIdRef.current;

      const fetchAbortCtrl = new AbortController();
      let fetchTimeoutId: ReturnType<typeof setTimeout> | null = setTimeout(() => {
        fetchAbortCtrl.abort(new Error('fetch_timeout'));
      }, FETCH_TIMEOUT_MS);

      const combinedSignal = (() => {
        if (typeof (AbortSignal as any).any === 'function') {
          return (AbortSignal as any).any([signal, fetchAbortCtrl.signal]) as AbortSignal;
        }
        return signal;
      })();

      let response: Response;
      try {
        response = await fetch(`${BASE_URL}/chat/endpoint`, {
          method: 'POST',
          headers: {
            ...authHeaders(resumeHeaders),
          },
          body: JSON.stringify({
            message: messageText,
            session_id: effectiveSessionId,
            language,
            client_message_uuid: clientMessageUuid,
            ...(lastEventId ? { last_event_id: lastEventId } : {}),
            ...(uploadedFiles && uploadedFiles.length > 0 ? { uploadedFiles } : {}),
          }),
          signal: combinedSignal,
        }).finally(() => {
          if (fetchTimeoutId) { clearTimeout(fetchTimeoutId); fetchTimeoutId = null; }
        });
      } catch (fetchErr: any) {
        cleanup();
        if (signal.aborted) return;
        if (retryCount < MAX_RETRY_ATTEMPTS) {
          errorFired = true; retryCount++;
          resetActiveBubble();
          const delay = Math.min(RETRY_BASE_DELAY * Math.pow(2, retryCount - 1), RETRY_MAX_DELAY);
          setTimeout(() => {
            errorFired = false;
            attemptStream().then(() => { outerSettled = true; _outerResolve(); }).catch(_outerReject);
          }, delay);
          return;
        }
        throw new Error('server_error');
      }

      if (!response.ok) {
        cleanup();
        let detail = `Error ${response.status}`;
        try { const err = await response.json(); detail = err.detail ?? detail; } catch { /* ignore */ }
        if (response.status === 401) throw new Error('auth');
        if (response.status === 429) throw new Error('rate_limit');
        if (response.status === 499) return;
        const isSessionMissing = (
          (response.status === 404 || response.status >= 500) &&
          detail.toLowerCase().includes('session') &&
          (detail.toLowerCase().includes('not found') || detail.toLowerCase().includes('expired'))
        );
        if (isSessionMissing && retryCount < MAX_RETRY_ATTEMPTS) {
          errorFired = true; retryCount++;
          resetActiveBubble();
          const delay = Math.min(RETRY_BASE_DELAY * Math.pow(2, retryCount - 1), RETRY_MAX_DELAY);
          setTimeout(() => {
            errorFired = false;
            attemptStream().then(() => { outerSettled = true; _outerResolve(); }).catch(_outerReject);
          }, delay);
          return;
        }
        if (response.status >= 500) throw new Error('server_error');
        throw new Error(detail);
      }
      if (!response.body) { cleanup(); throw new Error('No response body'); }

      const reader = response.body.getReader();
      readerRef = reader;
      const decoder = new TextDecoder();
      let buffer = '';

      scheduleStallTimeout(STALL_TIMEOUT_MS);

      const activeBotIdRef = { current: activeBotId };

      try {
        while (true) {
          let done: boolean;
          let value: Uint8Array | undefined;
          try {
            ({ done, value } = await reader.read());
          } catch { break; }
          if (done) break;
          onActivity();
          buffer += decoder.decode(value, { stream: true });
          const sseMessages = buffer.split('\n\n');
          buffer = sseMessages.pop() ?? '';
          for (const msg of sseMessages) {
            const lines = msg.split('\n');
            let eventId: string | null = null;
            let eventDataLine: string | null = null;
            for (const line of lines) {
              if (line.startsWith('id: '))        { eventId = line.slice(4).trim(); }
              else if (line.startsWith('data: ')) { eventDataLine = line; }
              else if (line.startsWith(': '))     { onActivity(); }
            }
            if (!eventDataLine) continue;
            try {
              const event = JSON.parse(eventDataLine.slice(6));
              if (eventId) lastEventId = eventId;

              const currentId = activeBotIdRef.current;

              if (event.data?.replayed === true) {
                setMessages(prev => prev.map(m => m.id !== activeBotIdRef.current ? m : finalizeMessage(m)));
                reader.cancel().catch(() => {}); cleanup(); _outerResolve(); return;
              }

              if (event.data?.is_final && event.data?.has_continuation) {
                const newBotId = `a_cont_${Date.now()}`;
                const newPlaceholder: Message = {
                  id: newBotId, sender: 'bot', text: '', vin: null, datasetId: null,
                  timestamp: new Date(), isStreaming: true, events: [],
                };
                const contFrame = parseStreamEvent(event) as ContinuationFrame;
                setMessages(prev => {
                  const updated = prev.map(m => m.id !== currentId ? m : reduceFrame(m, contFrame));
                  return [...updated, newPlaceholder];
                });
                activeBotIdRef.current = newBotId;
                activeBotId = newBotId;
                continue;
              }

              if (event.type === 'final') {
                if (event.data?.content) {
                  const _t = (event.data.content as string).trim();
                  if (_t.startsWith('{')) {
                    try {
                      const _p = JSON.parse(_t);
                      if (_p?.response_type === 'ERROR_HANDLED_BY_REFLECT_AND_RETRY_PLUGIN') {
                        reader.cancel().catch(() => {}); cleanup(); _outerReject(new Error('server_error')); return;
                      }
                    } catch { /* not JSON */ }
                  }
                }
                const frame = parseStreamEvent(event);
                const contFrame = { ...frame, kind: 'continuation' } as ContinuationFrame;
                setMessages(prev => prev.map(m => m.id !== activeBotIdRef.current ? m : reduceFrame(m, contFrame)));
                reader.cancel().catch(() => {}); cleanup(); _outerResolve(); return;
              }

              if (event.type === 'error') {
                const rawContent: string = event.data?.content ?? '';
                const ALLOWED = new Set(['session_not_found', 'auth', 'rate_limit', 'server_error']);
                const isSessionMiss = rawContent.toLowerCase().includes('not found') && rawContent.toLowerCase().includes('session');
                const errContent = isSessionMiss ? 'session_not_found' : ALLOWED.has(rawContent) ? rawContent : 'server_error';
                if (errContent === 'session_not_found' && retryCount < MAX_RETRY_ATTEMPTS) {
                  errorFired = true; readerRef?.cancel(); cleanup(); readerRef = null;
                  retryCount++; resetActiveBubble();
                  const delay = Math.min(RETRY_BASE_DELAY * Math.pow(2, retryCount - 1), RETRY_MAX_DELAY);
                  setTimeout(() => {
                    errorFired = false;
                    attemptStream().then(() => { outerSettled = true; _outerResolve(); }).catch(_outerReject);
                  }, delay);
                  return;
                }
                throw new Error(errContent);
              }

              if (event.data?.content) {
                const _trimmed = (event.data.content as string).trim();
                if (_trimmed.startsWith('{')) {
                  try {
                    const _p = JSON.parse(_trimmed);
                    if (_p?.response_type === 'ERROR_HANDLED_BY_REFLECT_AND_RETRY_PLUGIN') {
                      if (event.data?.is_final) throw new Error('server_error');
                      continue;
                    }
                  } catch (parseE) { if (!(parseE instanceof SyntaxError)) throw parseE; }
                }
              }

              {
                const frame = parseStreamEvent(event);
                if (frame.kind === 'intermediate' || frame.kind === 'continuation') {
                  setMessages(prev => prev.map(m => m.id !== activeBotIdRef.current ? m : reduceFrame(m, frame)));
                } else if (frame.kind === 'final') {
                  const asCont = { ...frame, kind: 'continuation' } as ContinuationFrame;
                  setMessages(prev => prev.map(m => m.id !== activeBotIdRef.current ? m : reduceFrame(m, asCont)));
                }
              }
            } catch (parseErr: any) {
              if (!(parseErr instanceof SyntaxError)) throw parseErr;
            }
          }
        }
      } finally {
        cleanup();
        try { reader.releaseLock(); } catch { /* already released */ }
      }

      setMessages(prev => prev.map(m => m.id !== activeBotIdRef.current ? m : finalizeMessage(m)));
      _outerResolve();
    };

    attemptStream()
      .then(() => { outerSettled = true; _outerResolve(); })
      .catch(err => { if (!outerSettled) _outerReject(err); });
    await outerPromise;
  }, [authHeaders, language]);

  // ── Friendly error messages ───────────────────────────────────────────────
  const friendlyErrorMessage = useCallback((e: any): string => {
    const msg: string = e?.message ?? String(e);
    if (msg === 'offline' || msg.includes('Failed to fetch') || msg.includes('NetworkError'))
      return 'You appear to be offline. Please check your connection and try again.';
    if (msg === 'auth') return 'Your session has expired. Please log in again.';
    if (msg === 'rate_limit') return 'Too many requests — please wait a moment and try again.';
    if (msg === 'server_error') return 'The server encountered an error. Please try again.';
    if (msg === 'session_not_found') return 'Your session has expired. Please refresh the page.';
    return 'Sorry, something went wrong. Please try again.';
  }, []);

  // ── Auto-trigger initial greeting ─────────────────────────────────────────
  const handleAutoTriggerNext = useCallback(async () => {
    const sid = sessionIdRef.current;
    if (isLoadingRef.current || !sid) return;

    const botMsgId = `a_next_${Date.now()}`;
    const botMsg: Message = {
      id: botMsgId, sender: 'bot', text: '', vin: null, datasetId: null,
      timestamp: new Date(), isStreaming: true, events: [],
    };

    setMessages(prev => [...prev, botMsg]);
    setIsLoading(true);
    isLoadingRef.current = true;

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      await streamToMessage('SYSTEM_ACTION:START_CHAT', botMsgId, controller.signal, undefined, sid);
    } catch (e: any) {
      if (e.name === 'AbortError') {
        setMessages(prev => prev.filter(m => m.id !== botMsgId));
        return;
      }
      const errorMsg = friendlyErrorMessage(e);
      setMessages(prev => prev.map(m =>
        m.id === botMsgId ? { ...m, isStreaming: false, isError: true, text: errorMsg } : m,
      ));
    } finally {
      setIsLoading(false);
      isLoadingRef.current = false;
      abortRef.current = null;
    }
  }, [streamToMessage, friendlyErrorMessage]);

  // ── Send message ──────────────────────────────────────────────────────────
  const sendMessage = async () => {
    if (!input.trim() && selectedFiles.length === 0) return;
    if (!sessionId || isLoadingRef.current) return;

    const filesToSend = selectedFiles.map(f => ({ data: f.data, mimeType: f.mimeType }));
    const messageText = input.trim();

    const userMsgId = `u_${Date.now()}`;
    const userMsg: Message = {
      id: userMsgId, sender: 'user', text: messageText,
      vin: null, datasetId: null, timestamp: new Date(), isStreaming: false,
      uploadedFiles: selectedFiles.length > 0 ? selectedFiles : undefined,
    };
    const assistantMsgId = `a_${Date.now()}`;
    const assistantMsg: Message = {
      id: assistantMsgId, sender: 'bot', text: '', vin: null, datasetId: null,
      timestamp: new Date(), isStreaming: true, events: [],
    };

    setMessages(prev => [...prev, userMsg, assistantMsg]);
    setInput('');
    setSelectedFiles([]);
    setIsLoading(true);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      await streamToMessage(messageText, assistantMsgId, controller.signal, filesToSend.length > 0 ? filesToSend : undefined);
    } catch (e: any) {
      if (e.name === 'AbortError') {
        setMessages(prev => prev.filter(m => m.id !== assistantMsgId));
        setInput(messageText);
        return;
      }
      const errorMsg = friendlyErrorMessage(e);
      setMessages(prev =>
        prev
          .filter(m => m.id !== assistantMsgId)
          .map(m => m.id === userMsgId ? { ...m, hasError: true, errorDetails: { message: errorMsg } } : m),
      );
    } finally {
      setIsLoading(false);
      abortRef.current = null;
    }
  };

  // ── Retry ─────────────────────────────────────────────────────────────────
  const handleRetry = useCallback(async (msg: Message) => {
    if (!sessionId || isLoadingRef.current) return;
    const messageText = msg.text ?? '';
    const retryFiles = (msg as any).uploadedFiles
      ? (msg as any).uploadedFiles.map((f: any) => ({ data: f.data, mimeType: f.mimeType }))
      : undefined;

    const newBotMsgId = `a_retry_${Date.now()}`;
    setMessages(prev => {
      const cleared = prev.map(m => m.id === msg.id ? { ...m, hasError: false, errorDetails: undefined } : m);
      return [...cleared, { id: newBotMsgId, sender: 'bot' as const, text: '', vin: null, datasetId: null, timestamp: new Date(), isStreaming: true, events: [] }];
    });

    setIsLoading(true);
    isLoadingRef.current = true;
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      await streamToMessage(messageText, newBotMsgId, controller.signal, retryFiles);
    } catch (e: any) {
      if (e.name === 'AbortError') {
        setMessages(prev => prev.filter(m => m.id !== newBotMsgId));
        return;
      }
      const errorMsg = friendlyErrorMessage(e);
      setMessages(prev =>
        prev
          .filter(m => m.id !== newBotMsgId)
          .map(m => m.id === msg.id ? { ...m, hasError: true, errorDetails: { message: errorMsg } } : m),
      );
    } finally {
      setIsLoading(false);
      isLoadingRef.current = false;
      abortRef.current = null;
    }
  }, [sessionId, streamToMessage, friendlyErrorMessage]);

  const handleBotRetry = useCallback((botMsg: Message) => {
    setMessages(prev => {
      const idx = prev.findIndex(m => m.id === botMsg.id);
      const prevUserMsg = idx > 0 ? [...prev.slice(0, idx)].reverse().find(m => m.sender === 'user') : undefined;
      if (prevUserMsg) {
        setTimeout(() => handleRetry(prevUserMsg), 0);
        return prev.filter(m => m.id !== botMsg.id);
      }
      setTimeout(() => handleAutoTriggerNext(), 0);
      return prev.filter(m => m.id !== botMsg.id);
    });
  }, [handleRetry, handleAutoTriggerNext]);

  // ── File handling ─────────────────────────────────────────────────────────
  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf', 'text/plain'];
    const maxSize = 10 * 1024 * 1024;
    Array.from(files).forEach(file => {
      if (!validTypes.includes(file.type)) { showToast(`File type not supported: ${file.name}`); return; }
      if (file.size > maxSize) { showToast(`File too large (max 10 MB): ${file.name}`); return; }
      const reader = new FileReader();
      reader.onload = (ev) => {
        const result = ev.target?.result as string;
        setSelectedFiles(prev => [...prev, { data: result.split(',')[1], mimeType: file.type, preview: result, name: file.name }]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  }, [showToast]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const noopFeedback = useCallback((_id: string, _helpful: boolean) => {}, []);
  const noopSearchPart = useCallback((text: string) => { if (text) setInput(text); }, []);
  const noopErrorToggle = useCallback((_id: string) => {}, []);
  const noopErrorCopy = useCallback((_details: any, _id: string) => {}, []);
  const noopAddToCart = useCallback((_part: any, _sd: any) => {}, []);
  const isValidImageData = useCallback((img: string | null) => !!img && img.length > 10, []);
  const errorTooltipRef = useRef<HTMLDivElement | null>(null);
  const expandedErrors = new Set<string>();

  const formatTime = useCallback((date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }, []);

  // ── Loading screen ────────────────────────────────────────────────────────
  if (initializing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="text-center space-y-5">
          <div className="relative mx-auto w-16 h-16">
            <div className="absolute inset-0 rounded-2xl bg-blue-500 opacity-20 animate-ping" />
            <div className="relative w-16 h-16 rounded-2xl bg-blue-600 flex items-center justify-center shadow-lg">
              <Bot className="w-8 h-8 text-white" />
            </div>
          </div>
          <p className="text-gray-400 text-xs">Starting your session…</p>
        </div>
      </div>
    );
  }

  if (initError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-red-50 px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center border border-red-100">
          <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-7 h-7 text-red-500" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Something went wrong</h2>
          <p className="text-sm text-gray-500 mb-6">{initError}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-5 py-2.5 bg-gray-900 text-white text-sm font-medium rounded-xl hover:bg-gray-700 transition-colors"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  // ── Main chat render ──────────────────────────────────────────────────────
  return (
    <OrderProvider>
      {/* Toast stack */}
      <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[300] flex flex-col gap-2 pointer-events-none w-full max-w-sm px-4">
        {toasts.map(toast => (
          <div
            key={toast.id}
            className={`flex items-center gap-2.5 px-4 py-3 rounded-xl shadow-lg text-sm font-medium pointer-events-auto
              ${toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-gray-900 text-white'}`}
          >
            {toast.type === 'error' ? <AlertCircle className="w-4 h-4 flex-shrink-0" /> : <Sparkles className="w-4 h-4 flex-shrink-0" />}
            <span className="flex-1">{toast.message}</span>
            <button onClick={() => setToasts(prev => prev.filter(t => t.id !== toast.id))} className="opacity-70 hover:opacity-100">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>

      <div className="flex flex-col overflow-hidden" style={{ height: '100dvh', background: '#f8f8f6' }}>
        {/* Offline banner */}
        {!isOnline && (
          <div className="flex-shrink-0 flex items-center justify-center gap-2 bg-amber-500 text-white text-xs font-semibold py-2 px-4">
            <WifiOff className="w-3.5 h-3.5 flex-shrink-0" />
            You appear to be offline — messages will send when reconnected.
          </div>
        )}

        {/* Header */}
        <header
          className="flex-shrink-0 bg-white border-b border-gray-100 px-4 py-3 flex items-center gap-3"
          style={{ boxShadow: '0 1px 0 0 #f0ede8' }}
        >
          <button
            onClick={() => navigate(`/portal/${orgAlias}/orders`)}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 transition-colors flex-shrink-0"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>

          <div className="w-9 h-9 rounded-xl bg-blue-600 flex items-center justify-center shadow-sm flex-shrink-0">
            <Bot className="w-5 h-5 text-white" />
          </div>

          <div className="flex-1 min-w-0">
            <h1 className="text-sm font-semibold text-gray-900 truncate leading-tight">AI Assistant</h1>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isOnline ? 'bg-emerald-400' : 'bg-amber-400'}`} />
              <span className="text-[11px] text-gray-400 leading-none">
                {isLoading ? 'Thinking…' : isOnline ? 'Online' : 'Offline'}
              </span>
            </div>
          </div>

          {/* Stop button when streaming */}
          {isLoading && abortRef.current && (
            <button
              onClick={() => { abortRef.current?.abort(); }}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-lg hover:bg-gray-200 transition-colors"
            >
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Stop
            </button>
          )}
        </header>

        {/* Messages */}
        <main
          ref={mainScrollRef as React.RefObject<HTMLElement>}
          className="flex-1 overflow-y-auto px-4 py-4 space-y-4"
        >
          <CatalogStatusStrip messages={messages} />
          {messages.length === 0 && !isLoading && (
            <div className="flex flex-col items-center justify-center h-full text-center py-16">
              <div className="w-16 h-16 rounded-2xl bg-blue-100 flex items-center justify-center mb-4">
                <Bot className="w-8 h-8 text-blue-500" />
              </div>
              <p className="text-sm font-medium text-gray-700">
                Hi{customer?.contact_name ? `, ${customer.contact_name}` : ''}!
              </p>
              <p className="text-xs text-gray-400 mt-1">How can I help you today?</p>
            </div>
          )}

          {messages.map((msg, idx) =>
            msg.sender === 'user' ? (
              <UserMessage
                key={msg.id}
                message={msg}
                isLoading={isLoading}
                isLastMessage={idx === messages.length - 1}
                expandedErrors={expandedErrors}
                errorTooltipRef={errorTooltipRef}
                onRetry={handleRetry}
                onToggleErrorDetails={noopErrorToggle}
                onCopyErrorDetails={noopErrorCopy}
                formatTime={formatTime}
              />
            ) : (
              <BotMessage
                key={msg.id}
                message={msg}
                persistedAnnotatedImages={annotatedImageAssignments.get(msg.id)}
                isLastMessage={idx === messages.length - 1}
                isLoading={isLoading}
                addingToCartItems={new Set()}
                onRetry={handleBotRetry}
                onFeedback={noopFeedback}
                onSearchPart={noopSearchPart}
                onAddToCart={noopAddToCart}
                formatTime={formatTime}
                isValidImageData={isValidImageData}
              />
            ),
          )}
          <div ref={bottomRef} />
        </main>

        {/* Scroll-to-bottom button */}
        {showScrollBtn && (
          <button
            onClick={() => scrollToBottom('smooth')}
            className="absolute bottom-24 right-4 z-10 w-8 h-8 bg-white rounded-full shadow-md flex items-center justify-center hover:bg-gray-50 transition-colors border border-gray-100"
          >
            <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        )}

        {/* Selected files preview */}
        {selectedFiles.length > 0 && (
          <div className="flex-shrink-0 bg-white border-t border-gray-100 px-4 py-2 flex flex-wrap gap-2">
            {selectedFiles.map((f, i) => (
              <div key={i} className="relative group flex items-center gap-1.5 bg-gray-100 rounded-lg px-2.5 py-1.5 text-xs text-gray-700 max-w-[160px]">
                {f.mimeType.startsWith('image/') ? (
                  <img src={f.preview} alt={f.name} className="w-5 h-5 rounded object-cover flex-shrink-0" />
                ) : (
                  <FileText className="w-4 h-4 flex-shrink-0 text-gray-500" />
                )}
                <span className="truncate">{f.name}</span>
                <button
                  onClick={() => setSelectedFiles(prev => prev.filter((_, idx) => idx !== i))}
                  className="ml-1 flex-shrink-0 text-gray-400 hover:text-gray-700"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Input bar */}
        <div
          className="flex-shrink-0 bg-white border-t border-gray-100 px-4 py-3"
          style={{ boxShadow: '0 -1px 0 0 #f0ede8' }}
        >
          <div className="flex items-end gap-2 bg-gray-50 rounded-2xl px-3 py-2 border border-gray-200 focus-within:border-blue-300 focus-within:ring-2 focus-within:ring-blue-100 transition-all">
            {/* File attachment */}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,.pdf,.txt"
              className="hidden"
              onChange={handleFileSelect}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-200 transition-colors mb-0.5"
              title="Attach file"
            >
              <Paperclip className="w-4 h-4" />
            </button>

            {/* Textarea */}
            <textarea
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type a message…"
              rows={1}
              disabled={isLoading}
              className="flex-1 bg-transparent resize-none outline-none text-sm text-gray-900 placeholder-gray-400 leading-6 max-h-32 py-1 disabled:opacity-50"
              style={{ scrollbarWidth: 'none' }}
            />

            {/* Send / stop */}
            <button
              onClick={isLoading ? () => abortRef.current?.abort() : sendMessage}
              disabled={!isLoading && !input.trim() && selectedFiles.length === 0}
              className={`flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-xl transition-all mb-0.5
                ${isLoading
                  ? 'bg-red-500 text-white hover:bg-red-600'
                  : (input.trim() || selectedFiles.length > 0)
                    ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm active:scale-95'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
            >
              {isLoading ? (
                <RotateCcw className="w-4 h-4" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>
      </div>
    </OrderProvider>
  );
};

export default PortalChatPage;
